import React, { useState } from "react";
import { Col, Row, Button } from "react-bootstrap";
import LicenceInformationTable from "./LicenceInformationProfile";
import LicenceModal from "./LicenceModel";
import {
  allLicenceDetails,
  editLicenceDetail,
  insertLicence,
  updateLicenceDetail,
} from "@/api/bid";
import { setAllLicenseInfo } from "@/bidFeature/propertyDetailSlice";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { trackApiCall, trackButtonClick } from "@/utils/analytics";

const LicenceInfoTable = ({
  handleShow,
  show,
  setShow,
  addLicenseInfo,
  handleSubmit,
  handleClose,
  formData,
  heading,
  setLicenseErrMsg,
  setLicenseCityDropDown,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showStateLicenseError, setShowStateLicenseError] = useState("");

  const [editData, setEditData] = useState(null);
  const dispatch = useDispatch();
  const licenceSelector = useSelector(
    (state) => state?.propertyDetail?.getAllLicenseInfo?.response
  );

  const handleEdit = async (id) => {
    trackButtonClick({
      eventName: "bid_license_edit_click",
      eventCategory: "Profile-Forms Bid/Offer wizard",
      eventLabel: `${id} | Agent | editLicense`,
      eventSource: "on edit license button click",
      eventParams: { license_id: id, license_type: "agent", license_scope: "individual" },
    });

    try {
      const response = await editLicenceDetail(id);
      // console.log("Raw API Response:", response); // Add this to see exact API response

      if (response?.status === 200) {
        // const formatDate = (dateString) => {
        //   if (!dateString) return "";
        //   const date = new Date(dateString);
        //   return `${(date.getMonth() + 1).toString().padStart(2, "0")}/${date
        //     .getDate()
        //     .toString()
        //     .padStart(2, "0")}/${date.getFullYear()}`;
        // };

        // Format the data to match your form structure
        const formattedData = {
          affiliatedCompany: response?.data?.company,
          agentLicenseState: response?.data?.state,
          licenceNumber: response?.data?.licenseNumber,
          licenceExpiryDate: response?.data?.expiryDate,
          address1: response?.data?.address1,
          address2: response?.data?.address2,
          city: response?.data?.city,
          zipcode: response?.data?.zip,
          status: response?.data?.status,
          licenceId: response?.data?.licenseId,
          state: response?.data?.stateId,
        };
        // console.log("formattedData", formattedData);
        setEditData(formattedData);
        setShow(true);
      }
    } catch (error) {
      console.error("Error fetching licence details:", error);
      // Add error notification here
    }
  };

  const handleFormSubmit = async (data) => {
    setLicenseErrMsg("");
    setIsLoading(true);

    try {
      const response = await insertLicence(data);
      if (response?.data?.status === "SUCCESS") {
        trackApiCall({
          eventName: "bid_license_add_click_success",
          eventCategory: "Profile-Forms Bid/Offer wizard",
          eventLabel: "Agent",
          eventSource: "on add license success",
          eventParams: { license_type: "agent", license_scope: "individual" },
        });

        setShow(false);
        const updatedLicenses = await allLicenceDetails();
        // console.log("Updated licenses:", updatedLicenses); // Check the data structure
        // const currentLicenses = licenceSelector || { INDV: [], CORP: [] };
        setShow(false);
        dispatch(
          setAllLicenseInfo({
            response: {
              CORP: updatedLicenses?.response?.CORP || [],
              INDV: updatedLicenses?.response?.INDV || [],
            },
          })
        );
        toast.success("License Added Successfully");
      } else {
        if (response?.data?.ERROR === "VALIDATION_STATE_ZIP_FAILURE") {
          setShowStateLicenseError(
            "Provided zip code is not valid for selected state"
          );
        } else {
          toast.error(response?.data?.ERROR);
          // setShowModal(false);
        }
      }
    } catch (error) {
      console.error("Error adding license:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditSubmit = async (updatedData) => {
    setLicenseErrMsg("");
    try {
      const payload = {
        ...updatedData,
        licenceId: editData.licenceId, // Ensure licenceId is included
      };
      const response = await updateLicenceDetail(payload);

      if (response?.status === 200 && response?.data?.status === "SUCCESS") {
        trackApiCall({
          eventName: "bid_license_update_click_success",
          eventCategory: "Profile-Forms Bid/Offer wizard",
          eventLabel: "Agent",
          eventSource: "on edit license success",
          eventParams: { license_id: editData?.licenceId, license_type: "agent", license_scope: "individual" },
        });
        const updatedLicenses = await allLicenceDetails();
        setShow(false);
        dispatch(
          setAllLicenseInfo({
            response: {
              CORP: updatedLicenses?.response?.CORP || [],
              INDV: updatedLicenses?.response?.INDV || [],
            },
          })
        );
        setEditData(null);
        toast.success("License Updated Successfully");
      } else {
        if (response?.data?.ERROR === "VALIDATION_STATE_ZIP_FAILURE") {
          setShowStateLicenseError(
            "Provided zip code is not valid for selected state"
          );
        } else {
          toast.error(response?.data?.ERROR);
          // setShowModal(false);
        }
      }
    } catch (error) {
      console.error("Error updating licence:", error);
      // alert("Error updating license. Please try again.");
    }
  };

  return (
    <div className="license-info">
      <Row className="d-flex justify-content-between align-items-center">
        <Col sm="auto">
          <h4 className="required-label bid-add-license-heading">
            {heading} <spana className="mandatory">*</spana>
          </h4>
        </Col>
        <Col sm="auto">
          <Button
            className="bid-add-license-btn"
            variant="primary"
            onClick={handleShow}
          >
            Add License
          </Button>
          <br></br>
        </Col>
        <LicenceInformationTable
          handleShow={handleShow}
          setShow={setShow}
          show={show}
          onEdit={handleEdit} // Pass the edit handler to table component
          formData={editData || formData}
          addLicenseInfo={addLicenseInfo}
        />
      </Row>
      <LicenceModal
        show={show}
        handleClose={() => {
          setShow(false);
          setEditData(null);
        }}
        addLicenseInfo={addLicenseInfo}
        handleSubmit={editData ? handleEditSubmit : handleFormSubmit} // Pass appropriate handler based on mode
        formData={editData || formData}
        editData={editData} // Pass edit data to modal
        isEditing={!!editData}
        setShowStateLicenseError={setShowStateLicenseError}
        showStateLicenseError={showStateLicenseError}
        setLicenseCityDropDown={setLicenseCityDropDown}
      />
    </div>
  );
};

export default LicenceInfoTable;
